import { useState } from "react"
import { useNavigate, useLocation, Navigate } from "react-router-dom"
import { useTranslations } from "next-intl"
import { Eye, EyeOff, Loader2, Lock, LogIn, ShieldCheck } from "lucide-react"
import { useAuthStore } from "@/stores/authStore"
import { Button } from "@/components/ui/button"

export default function LoginPage() {
	const t = useTranslations("login")
	const navigate = useNavigate()
	const location = useLocation()
	const { login, isLoading, isAuthenticated } = useAuthStore()

	const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/"

	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [showPassword, setShowPassword] = useState(false)
	const [error, setError] = useState("")

	if (isAuthenticated) return <Navigate to={from} replace />

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setError("")
		if (!email || !password) {
			setError(t("errors.required"))
			return
		}
		try {
			const success = await login(email, password)
			if (success) navigate(from, { replace: true })
			else setError(t("errors.invalid"))
		} catch {
			setError(t("errors.generic"))
		}
	}

	return (
		<div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 px-4">
			<div className="mb-8">
				<img src="/OptimGovLogo.png" alt="OptimGov" className="h-16" />
			</div>

			<div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
				<div className="bg-[#2D8A5E] px-6 py-4">
					<div className="flex items-center justify-center gap-2 text-white">
						<Lock className="h-5 w-5" />
						<span className="font-medium">{t("secureAccess")}</span>
					</div>
				</div>

				<div className="p-6 sm:p-8">
					<div className="text-center mb-6">
						<h1 className="text-2xl font-bold text-gray-700 mb-2">{t("title")}</h1>
						<p className="text-sm text-gray-400">{t("subtitle")}</p>
					</div>

					<form onSubmit={handleSubmit} className="space-y-5">
						{error && (
							<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
								{error}
							</div>
						)}

						<div className="space-y-2">
							<label htmlFor="email" className="block text-sm font-medium text-gray-500">
								{t("email")}
							</label>
							<input
								id="email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="block w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2D8A5E] focus:border-transparent transition-all duration-200 text-gray-700 placeholder:text-gray-400"
								placeholder={t("emailPlaceholder")}
								autoComplete="email"
							/>
						</div>

						<div className="space-y-2">
							<label htmlFor="password" className="block text-sm font-medium text-gray-500">
								{t("password")}
							</label>
							<div className="relative">
								<input
									id="password"
									type={showPassword ? "text" : "password"}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									className="block w-full px-4 py-3 pr-12 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2D8A5E] focus:border-transparent transition-all duration-200 text-gray-700 placeholder:text-gray-400"
									placeholder={t("passwordPlaceholder")}
									autoComplete="current-password"
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="absolute inset-y-0 right-0 pr-4 flex items-center"
								>
									{showPassword ? (
										<EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
									) : (
										<Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
									)}
								</button>
							</div>
						</div>

						<Button
							type="submit"
							size="lg"
							className="w-full py-3 text-base font-semibold bg-[#2D8A5E] hover:bg-[#2d5c3f] text-white"
							disabled={isLoading}
						>
							{isLoading ? (
								<><Loader2 className="mr-2 h-5 w-5 animate-spin" />{t("signingIn")}</>
							) : (
								<><LogIn className="mr-2 h-5 w-5" />{t("signIn")}</>
							)}
						</Button>
					</form>

					<div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">
						<ShieldCheck className="h-4 w-4" />
						<span>{t("secureConnection")}</span>
					</div>
				</div>
			</div>

			<div className="mt-8 text-center">
				<p className="text-sm text-gray-400">
					&copy; {new Date().getFullYear()} OptimGov. {t("rights")}
				</p>
			</div>
		</div>
	)
}
